import React, { useState } from "react";

import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { DisplayDate, DisplayDateTime } from "src/base-components/Date";
import { Label } from "src/base-components/Label";
import {
  ServiceAccountOAuthClientInfo,
  ServiceAccountOAuthClientKeyInfo,
} from "src/clients/taktile-api";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { ErrorHint } from "src/design-system/ErrorHint";
import { IconButton } from "src/design-system/Icon";
import { Input } from "src/design-system/Input";
import { Modal } from "src/design-system/Modal";
import { Pill } from "src/design-system/Pill";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import {
  useCreateOAuthClientKey,
  useDeleteOAuthClientKey,
} from "src/oauthClients/api/queries";
import { CredentialField } from "src/oauthClients/CredentialField";
import {
  KEY_LIFETIME_MAX_DAYS,
  keyLifetimeDaysError,
  MAX_ACTIVE_KEYS,
} from "src/oauthClients/constants";
import { KeyMaterialError } from "src/oauthClients/keyMaterial";
import { PrivateKeyHandoff } from "src/oauthClients/PrivateKeyHandoff";
import { PublicKeyFieldset } from "src/oauthClients/PublicKeyFieldset";
import {
  KeySupplyMode,
  resolveKeyMaterial,
} from "src/oauthClients/resolveKeyMaterial";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

type AddedKey = {
  kid: string;
  privateKeyPem?: string;
};

const LAST_KEY_TOOLTIP =
  "A client keeps at least one key. Add a replacement first.";
const NEW_KEY_TOOLTIP =
  "This is the key you just added. Reopen this dialog to remove it.";
const UNSAVED_KEY_TOOLTIP =
  "Confirm you have saved the new private key first. Removing this one now would leave the client with a key you cannot sign with.";

const KeyRow: React.FC<{
  isDeleting: boolean;
  isNew: boolean;
  keyInfo: ServiceAccountOAuthClientKeyInfo;
  /** Set when the key cannot go: doubles as the reason shown on the tooltip. */
  deleteBlockedReason?: string;
  onDelete: () => void;
}> = ({ isDeleting, isNew, keyInfo, deleteBlockedReason, onDelete }) => {
  const [isConfirming, setIsConfirming] = useState(false);

  return (
    <div
      className="flex items-center justify-between gap-x-2 rounded-lg border border-gray-200 px-3 py-2"
      data-loc={`oauth-key-row-${keyInfo.kid}`}
    >
      <div className="flex min-w-0 flex-col">
        <div className="flex items-center gap-x-2">
          <span className="break-all font-code-12 text-gray-800">
            {keyInfo.kid}
          </span>
          {isNew && (
            <Pill size="sm" variant="green">
              <Pill.Text>New</Pill.Text>
            </Pill>
          )}
        </div>
        <span className="font-inter-normal-12px text-gray-500">
          {keyInfo.last_used_at ? (
            <>
              Last used <DisplayDateTime value={keyInfo.last_used_at} />
            </>
          ) : (
            "Never used"
          )}
          <span className="px-1">·</span>
          Expires <DisplayDate value={keyInfo.expires_at} />
        </span>
      </div>
      {isConfirming ? (
        <div className="flex shrink-0 items-center gap-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsConfirming(false)}
          >
            Cancel
          </Button>
          <Button
            dataLoc={`oauth-key-remove-confirm-${keyInfo.kid}`}
            isLoading={isDeleting}
            size="sm"
            variant="warning"
            onClick={onDelete}
          >
            Remove
          </Button>
        </div>
      ) : (
        <Tooltip
          placement="top"
          title={deleteBlockedReason ?? "Remove key"}
          asChild
        >
          <IconButton
            dataLoc={`oauth-key-remove-${keyInfo.kid}`}
            disabled={Boolean(deleteBlockedReason)}
            icon={faTrashCan}
            size="xs"
            onClick={() => setIsConfirming(true)}
          />
        </Tooltip>
      )}
    </div>
  );
};

type Props = {
  client?: ServiceAccountOAuthClientInfo;
  open: boolean;
  organizationId: string;
  workspaceId: string;
  onClose: () => void;
  afterLeave: () => void;
};

export const RotateOAuthClientKeyModal: React.FC<Props> = ({
  client,
  open,
  organizationId: orgId,
  workspaceId: wsId,
  onClose,
  afterLeave,
}) => {
  const clientId = client?.client_id ?? "";
  const createKey = useCreateOAuthClientKey(orgId, wsId, clientId);
  const deleteKey = useDeleteOAuthClientKey(orgId, wsId, clientId);

  const [keySupplyMode, setKeySupplyMode] = useState<KeySupplyMode>("generate");
  const [pastedKey, setPastedKey] = useState("");
  const [lifetimeDays, setLifetimeDays] = useState("");
  const [error, setError] = useState<string>();
  const [addedKey, setAddedKey] = useState<AddedKey>();
  const [hasTakenKey, setHasTakenKey] = useState(false);
  const [isSaveConfirmed, setIsSaveConfirmed] = useState(false);
  const [deletingKid, setDeletingKid] = useState<string>();
  // Covers the whole submit, not just the mutation: key generation happens
  // before it and would otherwise leave the button live for a second click.
  const [isAdding, setIsAdding] = useState(false);

  const keys = client?.keys ?? [];
  const isAtKeyLimit = keys.length >= MAX_ACTIVE_KEYS && !addedKey;
  const isCloseBlocked = Boolean(addedKey?.privateKeyPem) && !isSaveConfirmed;

  const handleAfterLeave = () => {
    setKeySupplyMode("generate");
    setPastedKey("");
    setLifetimeDays("");
    setError(undefined);
    setAddedKey(undefined);
    setHasTakenKey(false);
    setIsSaveConfirmed(false);
    setDeletingKid(undefined);
    setIsAdding(false);
    afterLeave();
  };

  /**
   * taktile-api refuses to drop a client's only key. The key added in this
   * dialog is held back too: removing it would leave the close gate waiting on
   * a private key that no longer authenticates anything, with the add form
   * already hidden and no way out of the dialog.
   *
   * Every other key waits on the handoff. Until the customer confirms they hold
   * the new private key, the old one is the only credential their integration
   * can actually sign with, and dropping it would break live traffic in favour
   * of a key whose private half may be gone for good.
   */
  const deleteBlockedReason = (
    keyInfo: ServiceAccountOAuthClientKeyInfo,
  ): string | undefined => {
    if (keys.length <= 1) return LAST_KEY_TOOLTIP;
    if (keyInfo.kid === addedKey?.kid) return NEW_KEY_TOOLTIP;
    if (isCloseBlocked) return UNSAVED_KEY_TOOLTIP;
    return undefined;
  };

  const parsedLifetime = Number(lifetimeDays);
  const lifetimeError = keyLifetimeDaysError(lifetimeDays);

  const handleKeySupplyModeChange = (mode: KeySupplyMode) => {
    setKeySupplyMode(mode);
    setError(undefined);
  };

  const handlePastedKeyChange = (value: string) => {
    setPastedKey(value);
    setError(undefined);
  };

  const handleAddKey = async () => {
    if (isAdding || lifetimeError) return;
    setIsAdding(true);
    setError(undefined);

    try {
      const { publicJwk, privateKeyPem } = await resolveKeyMaterial(
        keySupplyMode,
        pastedKey,
      );
      const created = (
        await createKey.mutateAsync({
          public_jwk: publicJwk,
          ...(lifetimeDays ? { key_lifetime_days: parsedLifetime } : {}),
        })
      ).data;
      setAddedKey({ kid: created.key.kid, privateKeyPem });
    } catch (caught) {
      if (caught instanceof KeyMaterialError) {
        setError(caught.message);
        return;
      }
      logger.error(`Error when adding an OAuth client key: ${caught}`);
      toastActions.failure({
        title: "Failed to add the key",
        description: errorMessage(caught),
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteKey = async (kid: string) => {
    if (deletingKid) return;
    setDeletingKid(kid);
    try {
      await deleteKey.mutateAsync(kid);
      toastActions.success({ title: "Key removed" });
    } catch (caught) {
      logger.error(`Error when deleting an OAuth client key: ${caught}`);
      toastActions.failure({
        title: "Failed to remove the key",
        description: errorMessage(caught),
      });
    } finally {
      setDeletingKid(undefined);
    }
  };

  return (
    <Modal
      afterLeave={handleAfterLeave}
      closeMode={isCloseBlocked ? "persistent" : "standard"}
      open={open}
      onClose={onClose}
    >
      <Modal.Header
        description="Add a second key, move your integration onto it, then remove the old one. Both keys mint tokens while they coexist."
        hideCloseIcon={isCloseBlocked}
      >
        Rotate signing key
      </Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-5">
          {addedKey && (
            <>
              {addedKey.privateKeyPem && (
                <PrivateKeyHandoff
                  clientName={client?.client_name ?? ""}
                  dataLocPrefix="oauth-rotate"
                  hasTakenKey={hasTakenKey}
                  isSaveConfirmed={isSaveConfirmed}
                  privateKeyPem={addedKey.privateKeyPem}
                  onKeyTaken={() => setHasTakenKey(true)}
                  onSaveConfirmedChange={setIsSaveConfirmed}
                />
              )}
              <CredentialField
                dataLoc="oauth-rotate-new-kid"
                label="New key ID (kid)"
                value={addedKey.kid}
              />
            </>
          )}

          <div className="flex flex-col gap-2">
            <Label mb="mb-0">Keys on this client</Label>
            {keys.length === 0 ? (
              <span className="font-inter-normal-12px text-gray-500">
                This client holds no keys.
              </span>
            ) : (
              <>
                {keys.length > 1 && (
                  <span className="font-inter-normal-12px text-gray-500">
                    Remove a key once your integration no longer signs with it
                  </span>
                )}
                {keys.map((key) => (
                  <KeyRow
                    key={key.kid}
                    deleteBlockedReason={deleteBlockedReason(key)}
                    isDeleting={deletingKid === key.kid}
                    isNew={key.kid === addedKey?.kid}
                    keyInfo={key}
                    onDelete={() => handleDeleteKey(key.kid)}
                  />
                ))}
              </>
            )}
          </div>

          {!addedKey && (
            <>
              {isAtKeyLimit && (
                <Callout dataLoc="oauth-rotate-key-limit" variant="warning">
                  This client already holds {MAX_ACTIVE_KEYS} keys. Remove one
                  before adding another.
                </Callout>
              )}
              <PublicKeyFieldset
                error={error}
                mode={keySupplyMode}
                pastedKey={pastedKey}
                onModeChange={handleKeySupplyModeChange}
                onPastedKeyChange={handlePastedKeyChange}
              />
              <div className="flex flex-col gap-2">
                <Label mb="mb-0">Key lifetime in days</Label>
                <Input
                  aria-label="Key lifetime in days"
                  error={Boolean(lifetimeError)}
                  placeholder={`${KEY_LIFETIME_MAX_DAYS}`}
                  type="number"
                  value={lifetimeDays}
                  onChange={(event) => setLifetimeDays(event.target.value)}
                />
                {lifetimeError && <ErrorHint>{lifetimeError}</ErrorHint>}
              </div>
            </>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          addedKey ? (
            <Tooltip
              disabled={!isCloseBlocked}
              placement="top"
              title="Confirm you have saved the private key before closing"
              asChild
            >
              <Button
                dataLoc="oauth-rotate-done"
                isDisabled={isCloseBlocked}
                variant="primary"
                onClick={onClose}
              >
                Done
              </Button>
            </Tooltip>
          ) : (
            <Button
              dataLoc="oauth-rotate-add-key"
              isDisabled={isAtKeyLimit || Boolean(lifetimeError)}
              isLoading={isAdding}
              variant="primary"
              onClick={handleAddKey}
            >
              Add key
            </Button>
          )
        }
        secondaryButton={addedKey ? false : "Cancel"}
      />
    </Modal>
  );
};
